import { Outlet, useLocation, Link } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import './DashboardLayout.css';

// Session timeout settings
const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes
const MAX_SESSION_TIME = 30 * 60 * 1000; // 30 minutes

function DashboardLayout() {
    const location = useLocation();
    const [showLogoutWarning, setShowLogoutWarning] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);
    const [showProfile, setShowProfile] = useState(false);
    const [isSidebarMobileOpen, setIsSidebarMobileOpen] = useState(false);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [lastActivity, setLastActivity] = useState(Date.now());
    const [sessionStart] = useState(Date.now());

    const [notifications] = useState([
        {
            id: 1,
            type: 'payment',
            title: 'EMI Due Soon',
            message: 'Your Personal Loan EMI of ₹15,234 is due on 15 Dec 2025',
            time: '2 hours ago',
            read: false
        },
        {
            id: 2,
            type: 'success',
            title: 'Loan Application Approved',
            message: 'Your Two-Wheeler loan application has been approved!',
            time: '1 day ago',
            read: false
        },
        {
            id: 3,
            type: 'info',
            title: 'Credit Score Updated',
            message: 'Your credit score increased by 12 points this month',
            time: '2 days ago',
            read: true
        }
    ]);

    const [user, setUser] = useState({
        name: 'Loading...',
        email: '',
        phone: '',
        joinDate: '',
        kycStatus: 'Pending',
        accountStatus: 'Active',
        role: 'user'
    });

    const API_URL = process.env.REACT_APP_API_URL || '';

    useEffect(() => {
        const session = JSON.parse(localStorage.getItem('userSession'));
        console.log('🏗️ [FRONTEND-DASHBOARD] Initializing layout, session found:', !!session);
        if (session && session.userId) {
            const fetchUrl = `${API_URL}/api/auth/profile/${session.userId}`;
            console.log(`🌐 [FRONTEND-DASHBOARD] Fetching profile: ${fetchUrl}`);
            fetch(fetchUrl)
                .then(res => {
                    console.log(`📡 [FRONTEND-DASHBOARD] Profile response status: ${res.status}`);
                    return res.json();
                })
                .then(data => {
                    console.log('📦 [FRONTEND-DASHBOARD] Profile data:', data);
                    if (data.success) {
                        setUser(prev => ({
                            ...prev,
                            name: data.user.name,
                            email: data.user.email,
                            phone: data.user.phone,
                            joinDate: new Date(data.user.joinDate).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }),
                            kycStatus: data.user.kycStatus || 'In Progress',
                            accountStatus: 'Active',
                            role: data.user.role || 'user'
                        }));
                    }
                })
                .catch(err => console.error('❌ [FRONTEND-DASHBOARD] Profile fetch error:', err));
        }
    }, [API_URL]);

    const logoSrc = `${process.env.PUBLIC_URL || ''}/Png-01.png`;

    const sidebarLinks = [
        { path: '/dashboard', label: 'Dashboard', icon: '📊' },
        { path: '/dashboard/loans', label: 'My Loans', icon: '💰' },
        { path: '/dashboard/payments', label: 'Payments', icon: '💳' },
        { path: '/dashboard/documents', label: 'Documents', icon: '📄' },
        { path: '/dashboard/support', label: 'Support', icon: '💬' },
        { path: '/dashboard/profile', label: 'My Profile', icon: '👤' },
        { path: '/dashboard/kyc', label: 'KYC Verification', icon: '🆔' },
    ];

    const adminLinks = [
        { path: '/dashboard/admin/loans', label: 'Manage Loans', icon: '🛡️' },
        { path: '/dashboard/admin/documents', label: 'Manage Documents', icon: '📂' },
    ];

    const allLinks = (user.role === 'admin' || user.role === 'super_admin')
        ? [...sidebarLinks, ...adminLinks]
        : sidebarLinks;

    // Handle logout
    const handleLogout = useCallback(() => {
        // Save profile picture before clearing
        const profilePic = localStorage.getItem('profilePicture');

        localStorage.clear();
        sessionStorage.clear();

        // Restore profile picture
        if (profilePic) {
            localStorage.setItem('profilePicture', profilePic);
        }

        document.cookie.split(";").forEach((c) => {
            document.cookie = c
                .replace(/^ +/, "")
                .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });
        window.history.pushState(null, '', '/login');
        window.onpopstate = function () {
            window.history.pushState(null, '', '/login');
        };
        window.location.replace('/login');
    }, []);

    // Track activity and timeouts
    useEffect(() => {
        const updateActivity = () => setLastActivity(Date.now());
        const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
        events.forEach(event => window.addEventListener(event, updateActivity));

        const checkTimeout = setInterval(() => {
            const now = Date.now();
            if (now - sessionStart >= MAX_SESSION_TIME) {
                alert('Session expired. Please login again.');
                handleLogout();
            } else if (now - lastActivity >= INACTIVITY_TIMEOUT) {
                alert('Logged out due to inactivity.');
                handleLogout();
            }
        }, 60000);

        return () => {
            events.forEach(event => window.removeEventListener(event, updateActivity));
            clearInterval(checkTimeout);
        };
    }, [lastActivity, sessionStart, handleLogout]);

    const confirmLogout = () => {
        setShowLogoutWarning(false);
        handleLogout();
    };

    const unreadCount = notifications.filter(n => !n.read).length;

    return (
        <div className="dashboard-container">
            {/* Sidebar */}
            <aside className={`dashboard-sidebar ${isSidebarMobileOpen ? 'mobile-open' : ''} ${isSidebarCollapsed ? 'collapsed' : ''}`}>
                <div className="sidebar-brand">
                    <img src={logoSrc} alt="E-Fin" className="sidebar-logo" />
                    <button className="collapse-toggle" onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)} title={isSidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}>
                        {isSidebarCollapsed ? '❯' : '❮'}
                    </button>
                </div>

                <nav className="sidebar-nav">
                    {allLinks.map((link) => (
                        <Link
                            key={link.path}
                            to={link.path}
                            className={`nav-item ${location.pathname === link.path ? 'active' : ''}`}
                            onClick={() => setIsSidebarMobileOpen(false)}
                        >
                            <span className="nav-icon">{link.icon}</span>
                            <span className="nav-label">{link.label}</span>
                        </Link>
                    ))}
                </nav>

                <div className="sidebar-footer">
                    <button className="sidebar-logout-btn" onClick={() => setShowLogoutWarning(true)}>
                        <span className="nav-icon">🚪</span>
                        <span className="nav-label">Logout</span>
                    </button>
                </div>
            </aside>

            {/* Main Content Area */}
            <div className={`dashboard-wrapper ${isSidebarCollapsed ? 'collapsed' : ''}`}>
                {/* Top Header */}
                <header className="dashboard-header-new">
                    <div className="header-left">
                        <button className="mobile-toggle" onClick={() => setIsSidebarMobileOpen(!isSidebarMobileOpen)}>
                            ☰
                        </button>
                        <h2 className="page-title">
                            {allLinks.find(l => l.path === location.pathname)?.label || 'Dashboard'}
                        </h2>
                    </div>

                    <div className="header-right">
                        <div className="header-actions">
                            <button className="header-btn" onClick={() => {
                                setShowNotifications(!showNotifications);
                                setShowProfile(false);
                            }}>
                                <span className="icon">🔔</span>
                                {unreadCount > 0 && <span className="badge">{unreadCount}</span>}
                            </button>

                            <button className="header-profile-btn" onClick={() => {
                                setShowProfile(!showProfile);
                                setShowNotifications(false);
                            }}>
                                <div className="avatar">
                                    {user.name && user.name !== 'Loading...'
                                        ? user.name.split(' ').map(n => n[0]).filter(Boolean).join('').toUpperCase()
                                        : '☀️'}
                                </div>
                                <div className="name-info">
                                    <span className="user-name">{user.name}</span>
                                    <span className="user-role">{user.role === 'super_admin' ? 'Super Admin' : (user.role === 'admin' ? 'Administrator' : 'Customer')}</span>
                                </div>
                            </button>
                        </div>

                        {/* Notifications Dropdown */}
                        {showNotifications && (
                            <div className="header-dropdown notifications-dropdown">
                                <div className="dropdown-header">
                                    <h3>Notifications</h3>
                                    <button className="clear-all">Clear All</button>
                                </div>
                                <div className="dropdown-list">
                                    {notifications.map(n => (
                                        <div key={n.id} className={`dropdown-item ${n.read ? 'read' : 'unread'}`}>
                                            <div className="item-icon">{n.type === 'payment' ? '💳' : n.type === 'success' ? '✅' : 'ℹ️'}</div>
                                            <div className="item-body">
                                                <h4>{n.title}</h4>
                                                <p>{n.message}</p>
                                                <span className="time">{n.time}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Profile Dropdown */}
                        {showProfile && (
                            <div className="header-dropdown profile-dropdown">
                                <div className="profile-top">
                                    <div className="avatar-large">
                                        {user.name && user.name !== 'Loading...'
                                            ? user.name.split(' ').map(n => n[0]).filter(Boolean).join('').toUpperCase()
                                            : '🌙'}
                                    </div>
                                    <h4>{user.name}</h4>
                                    <p>{user.email}</p>
                                </div>
                                <div className="profile-menu">
                                    <Link to="/dashboard/profile" className="menu-item" onClick={() => setShowProfile(false)}>
                                        👤 My Profile
                                    </Link>
                                    <button className="menu-item" onClick={() => setShowLogoutWarning(true)}>
                                        🚪 Logout
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </header>

                <main className="dashboard-content-new">
                    <Outlet />
                </main>

                {/* Mobile Bottom Navigation */}
                <div className="mobile-bottom-nav">
                    <Link to="/dashboard" className={`bottom-nav-item ${location.pathname === '/dashboard' ? 'active' : ''}`}>
                        <span className="nav-icon">📊</span>
                        <span>Home</span>
                    </Link>
                    <Link to="/dashboard/loans" className={`bottom-nav-item ${location.pathname === '/dashboard/loans' ? 'active' : ''}`}>
                        <span className="nav-icon">💰</span>
                        <span>Loans</span>
                    </Link>
                    <Link to="/dashboard/payments" className={`bottom-nav-item ${location.pathname === '/dashboard/payments' ? 'active' : ''}`}>
                        <span className="nav-icon">💳</span>
                        <span>Pay</span>
                    </Link>
                    <Link to="/dashboard/support" className={`bottom-nav-item ${location.pathname === '/dashboard/support' ? 'active' : ''}`}>
                        <span className="nav-icon">💬</span>
                        <span>Help</span>
                    </Link>
                </div>
            </div>

            {/* Overlay for mobile sidebar */}
            {isSidebarMobileOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarMobileOpen(false)}></div>}

            {/* Logout Warning Modal */}
            {showLogoutWarning && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-icon warning">⚠️</div>
                        <h3>Confirm Logout</h3>
                        <p>Are you sure you want to log out of your dashboard?</p>
                        <div className="modal-actions">
                            <button className="modal-btn cancel" onClick={() => setShowLogoutWarning(false)}>
                                Cancel
                            </button>
                            <button className="modal-btn confirm" onClick={confirmLogout}>
                                Yes, Logout
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DashboardLayout;
